import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, WHATSAPP_LINK } from "../../data/content";

export const Wordmark = ({ dark = false }) => (
    <img
        src={dark ? "/logo-on-dark.png" : "/logo-on-light.png"}
        alt="ChatProz | AI Automation"
        className={`w-auto select-none ${dark ? "h-10" : "h-8"}`}
    />
);

const Nav = () => {
    const [open, setOpen] = useState(false);
    const lenis = useLenis();

    const go = (href) => (e) => {
        e.preventDefault();
        setOpen(false);
        const el = document.querySelector(href);
        if (!el) return;
        if (lenis) lenis.scrollTo(el, { offset: -76 });
        else el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/70" data-testid="site-nav">
            <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
                <a href="#top" onClick={go("#top")} data-testid="nav-logo" aria-label="ChatProz home">
                    <Wordmark />
                </a>

                <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={go(l.href)}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm font-medium text-slate-600 hover:text-navy transition-colors duration-200"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <motion.a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-cta"
                        whileTap={{ scale: 0.96 }}
                        className="hidden sm:inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy transition-colors duration-300"
                    >
                        Chat on WhatsApp
                        <ArrowUpRight className="w-4 h-4" />
                    </motion.a>
                    <button
                        onClick={() => setOpen(!open)}
                        data-testid="nav-menu-toggle"
                        aria-label="Toggle menu"
                        className="lg:hidden p-2 text-navy"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-xl"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-5 py-6 flex flex-col gap-5">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={go(l.href)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="font-display text-2xl font-semibold text-navy"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={WHATSAPP_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-whatsapp-cta"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 text-base font-semibold text-white"
                            >
                                Chat on WhatsApp
                                <ArrowUpRight className="w-5 h-5" />
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Nav;
